import { TFunction } from 'i18next';

import { Breakpoints } from '../../../enums';
import {
  PriceDataPoint,
  PriceDataPointWithPreviousClose,
} from '../../../types';
import {
  ChartGenerator,
  isDataSetSeriesGenerator,
  isExternalMarkerGenerator,
  isExternalSeriesGenerator,
  isSeriesGenerator,
  PriceViewerSeries,
  SeriesGenerator,
  TooltipCreator,
  TooltipFormatter,
} from '../generators/base';
import { ZoomButtonAction, ZoomLevel } from '../types';
import { makeCandleStockSeries } from './candlestick';
import {
  BRUSH_HEIGHT,
  CHART_TITLE_OFFSET,
  DEFAULT_SERIES_HEIGHT,
  DESKTOP_PRICE_CHART_HEIGHT,
  GRID_VERTICAL_MARGIN,
  INIT_CHART_HEIGHT,
  LEFT_SIDE_OFFSET,
  MIN_DESKTOP_PRICE_CHART_HEIGHT,
  MIN_MOBILE_PRICE_CHART_HEIGHT,
  MIN_VALUE_SPAN,
  MOBILE_PRICE_CHART_HEIGHT,
  RIGHT_SIDE_OFFSET,
  X_AXIS_FONT_SIZE,
  ZOOM_IN_BUTTON_ICON,
  ZOOM_OUT_BUTTON_ICON,
  ZOOM_RESTORE_BUTTON_ICON,
} from './constants';
import { paddedMax, paddedMin } from './extent';
import { embedIndexIntoMarkPointName } from './mark-point';
import { makeTooltip } from './tooltips';
import { optimizeSeriesWhenZoomingOut } from './zoomRange';

declare global {
  interface Window {
    _dlPriceYAxisMin: number;
    _dlPriceYAxisMax: number;
  }
}

window._dlPriceYAxisMin = 0;
window._dlPriceYAxisMax = 0;

const GET_PRICE_Y_AXIS_MIN = () => window._dlPriceYAxisMin;
const GET_PRICE_Y_AXIS_MAX = () => window._dlPriceYAxisMax;

const Y_AXIS_RANGE = {
  min: value => {
    window._dlPriceYAxisMin = paddedMin(value.min, value.max);
    return window._dlPriceYAxisMin;
  },
  max: value => {
    window._dlPriceYAxisMax = paddedMax(value.min, value.max);
    return window._dlPriceYAxisMax;
  },
};

/**
 * Generate the list of grid index, 0 is the OHLC candlestick grid.
 * if a generator is separate from price chart or it doesn't share y-axis with it,
 * it requires a new grid.
 */
const getGridIndices = (
  generators: SeriesGenerator[]
): {
  maxGridIndex: number;
  gridIndices: number[];
  subChartCount: number;
} => {
  let maxGridIndex = 0;
  let subChartCount = 0;
  const gridIndices: number[] = [];
  for (const generator of generators) {
    if (
      generator.isSeparateFromPriceChart ||
      !generator.shareYAxisWithPriceChart
    ) {
      if (generator.isSeparateFromPriceChart) {
        subChartCount++;
      }
      gridIndices.push(++maxGridIndex);
    } else {
      gridIndices.push(0);
    }
  }
  return { maxGridIndex, gridIndices, subChartCount };
};

const getGridSideMarginPercentage = (isMobile: boolean): string => {
  return isMobile ? '0%' : '80';
};

/**
 * Scale down the chart height if there are too many sub-charts
 */
const getPriceChartHeight = (
  priceChartHeight: number,
  numberOfSubCharts: number,
  isMobile: boolean
): number => {
  const priceChartMinHeight =
    window.innerWidth <= Breakpoints.small
      ? MIN_MOBILE_PRICE_CHART_HEIGHT
      : MIN_DESKTOP_PRICE_CHART_HEIGHT;
  const numberSubChartsToReduceHeight = isMobile ? 3 : 2;
  const reducedHeight =
    numberOfSubCharts > numberSubChartsToReduceHeight
      ? priceChartHeight -
        DEFAULT_SERIES_HEIGHT *
          (numberOfSubCharts - numberSubChartsToReduceHeight)
      : priceChartHeight;
  return reducedHeight >= priceChartMinHeight
    ? reducedHeight
    : priceChartMinHeight;
};

const makeTitlesAndGrids = (
  generators: SeriesGenerator[],
  gridIndices: number[],
  numberOfGrids: number,
  isMobile: boolean,
  hidePrice: boolean
): {
  titles: echarts.EChartTitleOption[];
  grids: echarts.EChartOption.Grid[];
  height: number;
} => {
  const priceChartHeight =
    window.innerWidth <= Breakpoints.small
      ? MOBILE_PRICE_CHART_HEIGHT
      : DESKTOP_PRICE_CHART_HEIGHT;

  const grids = [];
  const titles = [];
  let totalHeight = INIT_CHART_HEIGHT;

  // count number of sub charts and calc reduced price chart height if too many ( > 2)
  let numberOfSubCharts = 0;
  for (let i = 0; i < numberOfGrids; ++i) {
    const generator = generators[gridIndices.indexOf(i)];
    if (i !== 0 && generator && generator.isSeparateFromPriceChart) {
      numberOfSubCharts = numberOfSubCharts + 1;
    }
  }
  const reducedPriceChartHeightIfNeeded = hidePrice
    ? 1
    : getPriceChartHeight(priceChartHeight, numberOfSubCharts, isMobile);

  // generate the grid
  for (let i = 0; i < numberOfGrids; ++i) {
    const generator = generators[gridIndices.indexOf(i)];
    const height =
      i === 0 || generator.shareYAxisWithPriceChart || generator.height === -1
        ? reducedPriceChartHeightIfNeeded
        : generator.height;
    // if isSeparateFromPriceChart is false, the grid overlaps with the grid 0
    if (i === 0 || generator.isSeparateFromPriceChart) {
      grids.push({
        left: getGridSideMarginPercentage(isMobile),
        right: getGridSideMarginPercentage(isMobile),
        height,
        top: totalHeight,
      });
      // Give subchart titles
      if (i > 0) {
        titles.push({
          text: generator.getChartName(),
          left: 'center',
          top: totalHeight + CHART_TITLE_OFFSET,
          textStyle: {
            fontSize: 12,
          },
          padding: 0,
        });
      }
      totalHeight += height + GRID_VERTICAL_MARGIN;
      if (i === 0) {
        // Increase the margin for the first grid because it has x-axis labels
        // add 5 more pixels so that the texts do not get too close.
        totalHeight += X_AXIS_FONT_SIZE + 5;
      }
    } else {
      grids.push({
        left: getGridSideMarginPercentage(isMobile),
        right: getGridSideMarginPercentage(isMobile),
        height,
        top: INIT_CHART_HEIGHT + reducedPriceChartHeightIfNeeded - height,
      });
    }
  }
  return { titles, grids, height: totalHeight + BRUSH_HEIGHT };
};

interface Legend {
  icon: string;
  name: string;
  text: string;
  textStyle: {
    color: string;
  };
  itemStyle: {
    color: string;
  };
}

const makeAxises = (
  generators: SeriesGenerator[],
  gridIndices: number[],
  numberOfGrids: number,
  isMobile: boolean
): {
  xAxises: echarts.EChartOption.XAxis[];
  yAxises: echarts.EChartOption.YAxis[];
} => {
  // only first grid's axis needs labels
  const xAxises: echarts.EChartOption.XAxis[] = [];
  const yAxises: echarts.EChartOption.YAxis[] = [];
  for (let i = 0; i < numberOfGrids; ++i) {
    const generatorIndex = gridIndices.indexOf(i);
    let hideYAxisLabels = false;
    if (generatorIndex >= 0) {
      const generator = generators[generatorIndex];
      if (
        !generator.isSeparateFromPriceChart &&
        !generator.forceShowYAxisLabels &&
        !generator.shareYAxisWithPriceChart
      ) {
        hideYAxisLabels = true;
      }
    }
    xAxises.push({
      type: 'category',
      gridIndex: i,
      scale: true,
      boundaryGap: true,
      axisLine: { onZero: false },
      splitLine: { show: false },
      // put ticks and labels inside to prevent overflow and cover stuff outside.
      axisTick: { show: i === 0 },
      axisLabel: { show: i === 0, fontSize: X_AXIS_FONT_SIZE },
      splitNumber: 20,
      min: value => value.min - LEFT_SIDE_OFFSET,
      max: value => value.max + RIGHT_SIDE_OFFSET,
      axisPointer: {
        z: 100,
      },
    });
    let yAxisRange = {};
    // build a function to return the min an max of the yAxis
    if (i === 0) {
      yAxisRange = Y_AXIS_RANGE;
    }

    yAxises.push({
      ...yAxisRange,
      scale:
        generatorIndex >= 0
          ? !generators[generatorIndex].forceYAxisNotToScale
          : true,
      gridIndex: i,
      // after zlevel set to 1, the split line covers the series
      // which doesn't look good. Removing the lines and only keeps split areas.
      splitLine: {
        show: false,
      },
      axisLabel: {
        show: !hideYAxisLabels,
        inside: isMobile,
        formatter:
          generatorIndex >= 0 && generators[generatorIndex].yAxisFormatter,
      },
      // set z-level to 1, which is greater than series, so that series do not cover labels.
      zlevel: 1,
      // the price y-axis is on the right
      position: i === 0 ? 'right' : 'left',
    });
  }
  // Construct a separate non visible yAxis for sma-250/ema-250 with the range same as the main y-axis
  // So that the sma-250/ema250 max/min value won't affect the max/min of the main y-axis
  yAxises.push({
    scale: true,
    show: false,
    min: GET_PRICE_Y_AXIS_MIN,
    max: GET_PRICE_Y_AXIS_MAX,
    position: 'right',
  });
  return {
    xAxises,
    yAxises,
  };
};

const ma250series = (
  name: string,
  otherInfo: { xAxisIndex: number; yAxisIndex: number },
  yAxisLength: number
) => {
  return name === 'SMA-250' || name === 'EMA-250'
    ? {
        ...otherInfo,
        yAxisIndex: yAxisLength - 1,
      }
    : otherInfo;
};

const makeLegend = (
  generators: SeriesGenerator[],
  priceData: ReadonlyArray<PriceDataPoint>
) => {
  const legendList: Legend[] = [];
  for (const [, generator] of generators.entries()) {
    const seriesInThisGenerator = generator.getSeries(priceData);
    const seriesList = Array.isArray(seriesInThisGenerator)
      ? seriesInThisGenerator
      : [seriesInThisGenerator];
    for (const s of seriesList) {
      if (s.legendColor) {
        const text = getLegendText(s, priceData);
        legendList.push({
          icon: 'none',
          name: s.name,
          text: text,
          textStyle: {
            color: s.legendColor,
          },
          itemStyle: {
            color: s.legendColor,
          },
        });
      }
    }
  }
  return legendList;
};

/**
 * Go through all generators and extract series and tooltip formatters.
 * The tooltip formatters are stored in a map so that it can be found by series name.
 * @param generators list of generators
 * @param priceData price data
 * @param gridIndices grid indices for each generator
 * @returns [list of series, tooltip formatters mapped by series name]
 */
const extractFromGenerators = (
  generators: SeriesGenerator[],
  priceData: ReadonlyArray<PriceDataPoint>,
  gridIndices: number[],
  yAxisLength: number
): [
  echarts.EChartOption.Series[],
  echarts.EChartOption.VisualMap[],
  Record<string, TooltipFormatter>,
] => {
  if (generators.length !== gridIndices.length) {
    throw new Error('Generators and gridIndices have different lengths');
  }
  const visualMaps: echarts.EChartOption.VisualMap[] = [];
  const allSeries: echarts.EChartOption.Series[] = [];
  const tooltipFormatterBySeriesName: Record<string, TooltipFormatter> = {};

  for (const [i, generator] of generators.entries()) {
    const gridIndex = gridIndices[i];
    let otherInfo = {
      xAxisIndex: gridIndex,
      yAxisIndex: gridIndex,
    };

    let seriesInThisGenerator: PriceViewerSeries | PriceViewerSeries[] = [];
    if (isDataSetSeriesGenerator(generator)) {
      seriesInThisGenerator = generator.getSeries(priceData);
    }
    if (isExternalSeriesGenerator(generator)) {
      seriesInThisGenerator = generator.getSeries(priceData);
    }

    const seriesList = Array.isArray(seriesInThisGenerator)
      ? seriesInThisGenerator
      : [seriesInThisGenerator];
    for (const s of seriesList) {
      // handle sma 250 and ema 250 to not count in the chart yAxis
      otherInfo = ma250series(s.name, otherInfo, yAxisLength);
      allSeries.push({
        ...s.echartSeries,
        ...otherInfo,
        name: s.name,
      });

      if (generator.visualMap) {
        visualMaps.push({
          ...generator.visualMap(),
          seriesIndex: allSeries.length,
        });
      }
      tooltipFormatterBySeriesName[s.name] = s.tooltipFormatter;
    }
  }
  return [allSeries, visualMaps, tooltipFormatterBySeriesName];
};

const getLatestNonNullValue = (
  legendTextField: string,
  priceData: ReadonlyArray<PriceDataPointWithPreviousClose>
): number => {
  let latestNonNullValue: number;
  for (const data of [...priceData].reverse()) {
    if (!latestNonNullValue && data[legendTextField]) {
      latestNonNullValue = data[legendTextField];
      break;
    }
  }
  return latestNonNullValue;
};

const zoomToolBoxHeight = (subChartCount: number) => {
  if (subChartCount <= 2) {
    return 420;
  } else if (subChartCount > 2 && subChartCount < 4) {
    return 350;
  } else {
    return 280;
  }
};

const getLegendText = (
  s: PriceViewerSeries,
  priceData: ReadonlyArray<PriceDataPointWithPreviousClose>
): string => {
  if (s.legendTextField) {
    const data = getLatestNonNullValue(s.legendTextField, priceData);
    switch (s.legendFormat) {
      case 'integer': {
        return s.name + ': ' + data;
      }
      case 'percentage': {
        return s.name + ': ' + (data * 100).toFixed(2) + '%';
      }
      case 'float': {
        return s.name + ': ' + data.toFixed(2);
      }
      default: {
        return '';
      }
    }
  } else {
    const data = s.echartSeries.data.at(-1);
    if (data && data[1]) {
      return s.name + ': ' + data[1].toFixed(2);
    }
    return '';
  }
};

export const makeChart = (
  generators: ChartGenerator[],
  priceData: ReadonlyArray<PriceDataPointWithPreviousClose>,
  bullishColor: string,
  bearishColor: string,
  zoomLevel: ZoomLevel,
  t: TFunction,
  isMobile: boolean,
  markPointWidth: number,
  zoomButtonHandler: (ZoomButtonAction: ZoomButtonAction) => void,
  start = 90,
  end = 100,
  displayCornerData: boolean,
  displayMarkPointTooltip: boolean,
  hidePrice: boolean,
  shiftScrollToZoom: boolean
): {
  height: number;
  options: echarts.EChartOption | echarts.EChartsResponsiveOption;
  markTooltipCreaters: TooltipCreator[];
} => {
  const seriesGenerators = generators.filter(isSeriesGenerator);
  const { maxGridIndex, gridIndices, subChartCount } =
    getGridIndices(seriesGenerators);
  const { titles, grids, height } = makeTitlesAndGrids(
    seriesGenerators,
    gridIndices,
    maxGridIndex + 1,
    isMobile,
    hidePrice
  );
  const { xAxises, yAxises } = makeAxises(
    seriesGenerators,
    gridIndices,
    maxGridIndex + 1,
    isMobile
  );
  const [generatedSeries, visualMaps, tooltipFormatterBySeriesName] =
    extractFromGenerators(
      seriesGenerators,
      priceData,
      gridIndices,
      yAxises.length
    );
  //
  const legendList = makeLegend(seriesGenerators, priceData);
  const allGridIndices = Array.from({ length: maxGridIndex + 1 }).map(
    (_, i) => i
  );
  // marks
  const markGenerators = generators.filter(isExternalMarkerGenerator);

  const markPointData: echarts.EChartOption.SeriesCandlestick['markPoint']['data'][] =
    [];
  const markTooltipCreaters: TooltipCreator[] = [];

  for (const [i, generator] of markGenerators.entries()) {
    const { data, tooltipCreater } = generator.getMarkers(
      priceData,
      markPointWidth
    );
    for (const d of data) {
      markPointData.push({
        ...d,
        name: embedIndexIntoMarkPointName(d.name, i),
      });
    }
    markTooltipCreaters.push(tooltipCreater);
  }

  // Apply zooming out optimizations
  let finalSeries = generatedSeries;

  if (zoomLevel === 'zoomed-out') {
    finalSeries = generatedSeries.map(optimizeSeriesWhenZoomingOut);
  }

  const priceSeries = {
    ...makeCandleStockSeries(bullishColor, bearishColor),
    markPoint: {
      // we adjust the size dynamically, having animation is a bit choppy
      animation: false,
      data: markPointData,
    },
  } as echarts.EChartOption.Series; // "markPoint" doesn't exist on SeriesCustom but it still works

  return {
    options: {
      media: [
        // avoid trigger tooltip when swiping the stock chart on non desktop devices
        {
          query: { maxWidth: Breakpoints.medium },
          option: {
            tooltip: {
              triggerOn: 'click',
            },
          },
        },
      ],
      animation: true,
      animationThreshold: 500,
      dataset: {
        source: priceData,
      },
      tooltip:
        !displayMarkPointTooltip &&
        makeTooltip(tooltipFormatterBySeriesName, t, isMobile),
      axisPointer: {
        link: [{ xAxisIndex: 'all' }],
        label: {
          backgroundColor: '#777',
        },
      },
      toolbox: {
        show: isMobile ? false : true,
        left: 'center',
        top: zoomToolBoxHeight(subChartCount),
        itemSize: 25,
        feature: {
          myZoomOutButton: {
            show: true,
            icon: ZOOM_OUT_BUTTON_ICON,
            onclick: () => {
              zoomButtonHandler('zoom-out');
            },
          },
          myZoomRestoreButton: {
            show: true,
            icon: ZOOM_RESTORE_BUTTON_ICON,
            onclick: () => {
              zoomButtonHandler('zoom-restore');
            },
          },
          myZoomInButton: {
            show: true,
            icon: ZOOM_IN_BUTTON_ICON,
            onclick: () => {
              zoomButtonHandler('zoom-in');
            },
          },
          brush: { show: false },
        },
      },
      brush: {
        xAxisIndex: 'all',
        brushLink: 'all',
        outOfBrush: {
          colorAlpha: 0.1,
        },
      },
      legend: {
        show: displayCornerData,
        data: legendList,
        formatter: function (name) {
          const target = legendList.find(item => item.name === name);
          return `${target.text}`;
        },
        itemWidth: 0,
        icon: 'none',
        left: isMobile ? '15' : 'center',
        padding: 0,
        textStyle: {
          fontSize: 10,
        },
      },
      title: titles,
      grid: grids,
      xAxis: xAxises,
      yAxis: yAxises,
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: allGridIndices,
          start,
          end,
          minValueSpan: MIN_VALUE_SPAN,
          // do not block the default scroll event when performing a pan action
          // reference https://blog.csdn.net/qq_32580307/article/details/100012029
          preventDefaultMouseMove: false,
          zoomOnMouseWheel: shiftScrollToZoom ? ('shift' as any) : true, // echart types are wrong
        },
        {
          show: true,
          xAxisIndex: allGridIndices,
          type: 'slider',
          top: height - BRUSH_HEIGHT,
          start,
          end,
          minValueSpan: MIN_VALUE_SPAN,
        },
      ],
      visualMap: visualMaps,
      series: [priceSeries, ...finalSeries],
    },
    height,
    markTooltipCreaters,
  };
};
